import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { config } from '../../config';

export default function DashboardPat() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [periode, setPeriode] = useState('jour');

  const user = JSON.parse(localStorage.getItem('user') || '{}');

  // Utilisation de la configuration centralisée
  const API_BASE = config.apiBase;

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');

      const response = await fetch(`${API_BASE}/pat/dashboard_stats.php`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (data.success) {
        setStats(data.stats);
      } else {
        toast.error(data.message || 'Erreur de chargement');
      }
    } catch (error) {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const formatMoney = (value) => {
    return Number(value || 0).toLocaleString('fr-FR') + ' F';
  };

  const getTotal = (key) => {
    if (!stats?.totaux?.[key]) {
      return { nombre: 0, total: 0 };
    }

    return stats.totaux[key];
  };

  const StatCard = ({ title, amount, count, color }) => (
    <div className={`bg-white rounded-xl shadow-sm border-l-4 ${color.border} p-5`}>
      <p className="text-sm text-gray-500">{title}</p>
      <p className={`text-3xl font-black mt-2 ${color.text}`}>{formatMoney(amount)}</p>
      <p className="text-xs text-gray-400 mt-1">{count} opération(s)</p>
    </div>
  );

  const TableStats = ({ title, rows, color }) => (
    <div className="bg-white rounded-xl shadow-sm overflow-hidden">
      <div className={`px-5 py-4 ${color.header}`}>
        <h2 className="font-bold text-white">{title}</h2>
      </div>

      {rows.length === 0 ? (
        <div className="p-8 text-center text-gray-400">
          Aucune donnée pour cette période
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-5 py-3 text-left text-xs font-bold text-gray-500 uppercase">
                  Institution
                </th>
                <th className="px-5 py-3 text-right text-xs font-bold text-gray-500 uppercase">
                  Nombre
                </th>
                <th className="px-5 py-3 text-right text-xs font-bold text-gray-500 uppercase">
                  Total
                </th>
              </tr>
            </thead>

            <tbody className="divide-y divide-gray-100">
              {rows.map((row, index) => (
                <tr key={`${row.institution}-${index}`} className="hover:bg-gray-50">
                  <td className="px-5 py-4 font-medium text-gray-800">
                    {row.institution}
                  </td>
                  <td className="px-5 py-4 text-right text-gray-600">
                    {Number(row.nombre || 0).toLocaleString('fr-FR')}
                  </td>
                  <td className={`px-5 py-4 text-right font-bold ${color.text}`}>
                    {formatMoney(row.total)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-700"></div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="p-8">
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-6 text-center">
          Impossible de charger les statistiques.
        </div>
      </div>
    );
  }

  const rechargeTotal = getTotal(`recharges_${periode}`);
  const repasTotal = getTotal(`repas_${periode}`);
  const busTotal = getTotal(`bus_${periode}`);

  return (
    <div className="min-h-screen bg-slate-50 p-6 md:p-8">
      <div className="mb-8">
        <h1 className="text-2xl md:text-3xl font-black text-blue-900">
          Bonjour, {user.prenom} {user.nom}
        </h1>
        <p className="text-gray-500 mt-1">
          Statistiques des recharges et débits classées par institution
        </p>
      </div>

      <div className="mb-6 flex flex-wrap gap-2">
        <button
          onClick={() => setPeriode('jour')}
          className={`px-4 py-2 rounded-lg font-semibold transition ${
            periode === 'jour'
              ? 'bg-blue-700 text-white'
              : 'bg-white text-gray-600 hover:bg-blue-50'
          }`}
        >
          Aujourd'hui
        </button>

        <button
          onClick={() => setPeriode('mois')}
          className={`px-4 py-2 rounded-lg font-semibold transition ${
            periode === 'mois'
              ? 'bg-blue-700 text-white'
              : 'bg-white text-gray-600 hover:bg-blue-50'
          }`}
        >
          Ce mois
        </button>

        <button
          onClick={() => setPeriode('annee')}
          className={`px-4 py-2 rounded-lg font-semibold transition ${
            periode === 'annee'
              ? 'bg-blue-700 text-white'
              : 'bg-white text-gray-600 hover:bg-blue-50'
          }`}
        >
          Cette année
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-8">
        <StatCard
          title="Recharges"
          amount={rechargeTotal.total}
          count={rechargeTotal.nombre}
          color={{
            border: 'border-green-500',
            text: 'text-green-700',
          }}
        />

        <StatCard
          title="Débits repas"
          amount={repasTotal.total}
          count={repasTotal.nombre}
          color={{
            border: 'border-red-500',
            text: 'text-red-600',
          }}
        />

        <StatCard
          title="Débits bus"
          amount={busTotal.total}
          count={busTotal.nombre}
          color={{
            border: 'border-blue-500',
            text: 'text-blue-700',
          }}
        />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <TableStats
          title="Recharges par institution"
          rows={stats.recharges[periode] || []}
          color={{
            header: 'bg-green-600',
            text: 'text-green-700',
          }}
        />

        <TableStats
          title="Débits repas par institution"
          rows={stats.debits.repas[periode] || []}
          color={{
            header: 'bg-red-600',
            text: 'text-red-600',
          }}
        />

        <TableStats
          title="Débits bus par institution"
          rows={stats.debits.bus[periode] || []}
          color={{
            header: 'bg-blue-700',
            text: 'text-blue-700',
          }}
        />
      </div>
    </div>
  );
}